/**
 * Client for GET /api/data/event-race-summary and event-race-summary-events.
 */
import { apiEndpoints } from "@config/env";
import { getData } from "../../../utils/global";
import { error as logError } from "../../../utils/console";

export interface EventRaceSummaryTeam {
  source_name: string;
  races_sailed: number;
  races_finished: number;
  mean_position: number | null;
  std_position: number | null;
  podium_perc: number | null;
  mean_time_behind_leader_sec: number | null;
  gap_start_m_mean: number | null;
  gap_start_m_std: number | null;
  gap_mnvr_m_mean: number | null;
  gap_mnvr_m_std: number | null;
  gap_marks_m_mean: number | null;
  gap_marks_m_std: number | null;
  gap_side_m_mean: number | null;
  gap_side_m_std: number | null;
  gap_speed_m_mean: number | null;
  gap_speed_m_std: number | null;
  mean_inphase_perc: number | null;
  mean_distance_ratio: number | null;
  mean_left_side_perc: number | null;
  mean_shift_gain_m_per_deg: number | null;
  marks_loss_per_rounding_m: number | null;
  mnvr_loss_per_tg_m: number | null;
  /** Mean place off the line. 1 is fastest toward mark 1 in the first 10 seconds of that start. */
  mean_start_rank: number | null;
  /** Starts that contributed to mean_start_rank. Weights the season merge. */
  start_rank_n: number;
}

export interface EventRaceSummaryRaceMeta {
  date_ymd: string;
  race_key: string;
  label: string;
}

export interface EventRaceSummaryCell {
  date_ymd: string;
  race_key: string;
  position: number | null;
  gap_total_m: number | null;
  dominant_loss: string | null;
  race_status: string | null;
}

export interface EventRaceSummaryPayload {
  event_name: string;
  year_name: string | null;
  teams: EventRaceSummaryTeam[];
  races: EventRaceSummaryRaceMeta[];
  matrix: { source_name: string; cells: EventRaceSummaryCell[] }[];
  benchmark: { best: Record<string, number | null>; top3_avg: Record<string, number | null> };
  fingerprint?: { row_count: number; date_min: string | null; date_max: string | null };
  cache_hit?: boolean;
}

export interface EventNameOption {
  event_name: string;
  year_name: string | null;
}

export async function fetchEventRaceSummaryEvents(
  className: string,
  projectId: number | string,
  signal?: AbortSignal
): Promise<EventNameOption[]> {
  const url =
    `${apiEndpoints.app.data}/event-race-summary-events` +
    `?class_name=${encodeURIComponent(className)}` +
    `&project_id=${encodeURIComponent(String(projectId))}`;
  try {
    const result = await getData(url, signal);
    if (!result?.success) return [];
    const events = (result.data as { events?: EventNameOption[] })?.events;
    return Array.isArray(events) ? events : [];
  } catch (err) {
    logError("eventRaceSummaryApi: fetch events failed", err);
    return [];
  }
}

export async function fetchEventRaceSummary(
  className: string,
  projectId: number | string,
  eventName: string,
  yearName: string | null,
  signal?: AbortSignal
): Promise<EventRaceSummaryPayload | null> {
  let url =
    `${apiEndpoints.app.data}/event-race-summary` +
    `?class_name=${encodeURIComponent(className)}` +
    `&project_id=${encodeURIComponent(String(projectId))}` +
    `&event_name=${encodeURIComponent(eventName)}`;
  if (yearName) url += `&year_name=${encodeURIComponent(yearName)}`;
  try {
    const result = await getData(url, signal);
    if (result?.type === "AbortError") {
      const cancelled = new Error("Request cancelled");
      cancelled.name = "AbortError";
      throw cancelled;
    }
    if (!result?.success) return null;
    return result.data as EventRaceSummaryPayload;
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") throw err;
    logError("eventRaceSummaryApi: fetch summary failed", err);
    return null;
  }
}

function placesAreRanking(places: number[]): boolean {
  if (places.length < 2) return false;
  const seen = new Set<number>();
  let min = Infinity;
  let max = -Infinity;
  for (const place of places) {
    if (seen.has(place)) return false;
    seen.add(place);
    if (place < min) min = place;
    if (place > max) max = place;
  }
  return min === 1 && max === places.length;
}

function meanOf(values: number[]): number | null {
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function stdOf(values: number[]): number | null {
  if (values.length < 2) return null;
  const avg = meanOf(values);
  if (avg == null) return null;
  const variance = values.reduce((sum, value) => sum + (value - avg) ** 2, 0) / (values.length - 1);
  return Math.sqrt(variance);
}

function cellFinished(cell: EventRaceSummaryCell | undefined): boolean {
  if (!cell || cell.race_status === "dnf" || cell.race_status === "cut") return false;
  return cell.position != null && Number.isFinite(cell.position);
}

/**
 * Drop races that do not have places 1..n once each, and drop teams with no finish in what remains.
 * Place totals are recomputed from the races that stay.
 */
export function rankedEventSummary(payload: EventRaceSummaryPayload): EventRaceSummaryPayload {
  const races = payload.races || [];
  const matrix = payload.matrix || [];
  const keepIdx: number[] = [];
  races.forEach((_, index) => {
    const places: number[] = [];
    for (const row of matrix) {
      const cell = row.cells?.[index];
      if (cellFinished(cell)) places.push(Number(cell!.position));
    }
    if (placesAreRanking(places)) keepIdx.push(index);
  });
  if (keepIdx.length === races.length && races.length > 0) return payload;

  const nextRaces = keepIdx.map((index) => races[index]);
  const blankCell = (index: number): EventRaceSummaryCell => ({
    date_ymd: races[index]?.date_ymd || "",
    race_key: races[index]?.race_key || "",
    position: null,
    gap_total_m: null,
    dominant_loss: null,
    race_status: null,
  });
  const nextMatrix = matrix
    .map((row) => ({
      source_name: row.source_name,
      cells: keepIdx.map((index) => row.cells[index] ?? blankCell(index)),
    }))
    .filter((row) => row.cells.some((cell) => cellFinished(cell)));

  const byName = new Map(nextMatrix.map((row) => [row.source_name, row]));
  const teams = (payload.teams || [])
    .filter((team) => byName.has(team.source_name))
    .map((team) => {
      const cells = byName.get(team.source_name)?.cells ?? [];
      const places = cells.filter((cell) => cellFinished(cell)).map((cell) => Number(cell.position));
      const podium = places.filter((place) => place >= 1 && place <= 3).length;
      return {
        ...team,
        races_sailed: cells.filter((cell) => cell.position != null || cell.race_status != null).length,
        races_finished: places.length,
        mean_position: meanOf(places),
        std_position: stdOf(places),
        podium_perc: places.length ? (100 * podium) / places.length : null,
        // Part means were averaged over races this filter just dropped.
        gap_start_m_mean: null,
        gap_mnvr_m_mean: null,
        gap_marks_m_mean: null,
        gap_side_m_mean: null,
        gap_speed_m_mean: null,
        gap_start_m_std: null,
        gap_mnvr_m_std: null,
        gap_marks_m_std: null,
        gap_side_m_std: null,
        gap_speed_m_std: null,
        // Ranked on the races this filter just dropped.
        mean_start_rank: null,
        start_rank_n: 0,
      };
    })
    .sort((a, b) => {
      if (a.mean_position == null && b.mean_position == null) return a.source_name.localeCompare(b.source_name);
      if (a.mean_position == null) return 1;
      if (b.mean_position == null) return -1;
      return a.mean_position - b.mean_position;
    });

  const order = new Map(teams.map((team, index) => [team.source_name, index]));
  nextMatrix.sort((a, b) => (order.get(a.source_name) ?? 999) - (order.get(b.source_name) ?? 999));

  const nums = (key: keyof EventRaceSummaryTeam) =>
    teams
      .map((team) => team[key])
      .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  const best = { ...(payload.benchmark?.best || {}) };
  const meanPos = nums("mean_position");
  const stdPos = nums("std_position");
  const podium = nums("podium_perc");
  const finished = nums("races_finished");
  if (meanPos.length) best.mean_position = Math.min(...meanPos);
  if (stdPos.length) best.std_position = Math.min(...stdPos);
  if (podium.length) best.podium_perc = Math.max(...podium);
  if (finished.length) best.races_finished = Math.max(...finished);
  best.mean_start_rank = null;

  return {
    ...payload,
    teams,
    races: nextRaces,
    matrix: nextMatrix,
    benchmark: { best, top3_avg: payload.benchmark?.top3_avg || {} },
  };
}

const WEIGHTED_MEAN_KEYS = [
  "mean_time_behind_leader_sec",
  "gap_start_m_mean",
  "gap_mnvr_m_mean",
  "gap_marks_m_mean",
  "gap_side_m_mean",
  "gap_speed_m_mean",
  "mean_inphase_perc",
  "mean_distance_ratio",
  "mean_left_side_perc",
  "mean_shift_gain_m_per_deg",
  "marks_loss_per_rounding_m",
  "mnvr_loss_per_tg_m",
] as const satisfies readonly (keyof EventRaceSummaryTeam)[];

const HIGHER_BETTER = new Set<keyof EventRaceSummaryTeam>([
  "podium_perc",
  "mean_inphase_perc",
  "mean_shift_gain_m_per_deg",
  "races_finished",
]);

function weightedStartRank(teams: EventRaceSummaryTeam[]): { mean: number | null; n: number } {
  let sum = 0;
  let weight = 0;
  for (const team of teams) {
    const value = team.mean_start_rank;
    const races = team.start_rank_n;
    if (typeof value !== "number" || !Number.isFinite(value) || !races) continue;
    sum += value * races;
    weight += races;
  }
  return { mean: weight ? sum / weight : null, n: weight };
}

function weightedMean(teams: EventRaceSummaryTeam[], key: (typeof WEIGHTED_MEAN_KEYS)[number]): number | null {
  let sum = 0;
  let weight = 0;
  for (const team of teams) {
    const value = team[key];
    const races = team.races_finished;
    if (typeof value !== "number" || !Number.isFinite(value) || !races) continue;
    sum += value * races;
    weight += races;
  }
  return weight ? sum / weight : null;
}

/**
 * One season from several event summaries. Each event is filtered to real finish orders first.
 * Place totals are counted across those races. Other means are weighted by races finished.
 */
export function mergeSeasonSummaries(
  payloads: EventRaceSummaryPayload[],
  yearName: string | null
): EventRaceSummaryPayload | null {
  const ranked = payloads
    .filter((payload) => payload && (payload.races?.length || payload.teams?.length))
    .map((payload) => rankedEventSummary(payload));
  const withRaces = ranked.filter((payload) => payload.races.length > 0);
  if (!withRaces.length) return null;
  if (withRaces.length === 1) {
    return { ...withRaces[0], event_name: "All", year_name: yearName ?? withRaces[0].year_name };
  }

  const columns: {
    meta: EventRaceSummaryRaceMeta;
    byTeam: Map<string, EventRaceSummaryCell>;
  }[] = [];
  for (const part of withRaces) {
    part.races.forEach((race, index) => {
      const byTeam = new Map<string, EventRaceSummaryCell>();
      for (const row of part.matrix) {
        const cell = row.cells[index];
        if (cell) byTeam.set(row.source_name, cell);
      }
      columns.push({ meta: race, byTeam });
    });
  }
  columns.sort((a, b) => {
    if (a.meta.date_ymd !== b.meta.date_ymd) return a.meta.date_ymd.localeCompare(b.meta.date_ymd);
    return Number(a.meta.race_key) - Number(b.meta.race_key);
  });

  const blank = (meta: EventRaceSummaryRaceMeta): EventRaceSummaryCell => ({
    date_ymd: meta.date_ymd,
    race_key: meta.race_key,
    position: null,
    gap_total_m: null,
    dominant_loss: null,
    race_status: null,
  });
  const teamNames = new Set<string>();
  for (const column of columns) {
    for (const name of column.byTeam.keys()) teamNames.add(name);
  }
  const matrix = [...teamNames]
    .map((source_name) => ({
      source_name,
      cells: columns.map((column) => column.byTeam.get(source_name) ?? blank(column.meta)),
    }))
    .filter((row) => row.cells.some((cell) => cellFinished(cell)));

  const prior = new Map<string, EventRaceSummaryTeam[]>();
  for (const part of withRaces) {
    for (const team of part.teams) {
      const list = prior.get(team.source_name) ?? [];
      list.push(team);
      prior.set(team.source_name, list);
    }
  }

  const teams: EventRaceSummaryTeam[] = matrix
    .map((row) => {
      const places = row.cells.filter((cell) => cellFinished(cell)).map((cell) => Number(cell.position));
      const podium = places.filter((place) => place >= 1 && place <= 3).length;
      const sources = prior.get(row.source_name) ?? [];
      const team: EventRaceSummaryTeam = {
        source_name: row.source_name,
        races_sailed: row.cells.filter((cell) => cell.position != null || cell.race_status != null).length,
        races_finished: places.length,
        mean_position: meanOf(places),
        std_position: stdOf(places),
        podium_perc: places.length ? (100 * podium) / places.length : null,
        mean_time_behind_leader_sec: null,
        gap_start_m_mean: null,
        gap_start_m_std: null,
        gap_mnvr_m_mean: null,
        gap_mnvr_m_std: null,
        gap_marks_m_mean: null,
        gap_marks_m_std: null,
        gap_side_m_mean: null,
        gap_side_m_std: null,
        gap_speed_m_mean: null,
        gap_speed_m_std: null,
        mean_inphase_perc: null,
        mean_distance_ratio: null,
        mean_left_side_perc: null,
        mean_shift_gain_m_per_deg: null,
        marks_loss_per_rounding_m: null,
        mnvr_loss_per_tg_m: null,
        mean_start_rank: null,
        start_rank_n: 0,
      };
      for (const key of WEIGHTED_MEAN_KEYS) team[key] = weightedMean(sources, key);
      const start = weightedStartRank(sources);
      team.mean_start_rank = start.mean;
      team.start_rank_n = start.n;
      return team;
    })
    .sort((a, b) => {
      if (a.mean_position == null && b.mean_position == null) return a.source_name.localeCompare(b.source_name);
      if (a.mean_position == null) return 1;
      if (b.mean_position == null) return -1;
      return a.mean_position - b.mean_position;
    });

  const order = new Map(teams.map((team, index) => [team.source_name, index]));
  matrix.sort((a, b) => (order.get(a.source_name) ?? 999) - (order.get(b.source_name) ?? 999));

  const best: Record<string, number | null> = {};
  const keys = Object.keys(teams[0] || {}).filter((key) => key !== "source_name") as (keyof EventRaceSummaryTeam)[];
  for (const key of keys) {
    const values = teams
      .map((team) => team[key])
      .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
    if (!values.length) {
      best[key] = null;
      continue;
    }
    best[key] = HIGHER_BETTER.has(key) ? Math.max(...values) : Math.min(...values);
  }

  return {
    event_name: "All",
    year_name: yearName,
    teams,
    races: columns.map((column) => column.meta),
    matrix,
    benchmark: { best, top3_avg: {} },
  };
}

/**
 * Mean and sample σ of finish gap on the races this team finished in the matrix.
 * σ needs two finishes, so a one-race team is omitted.
 */
export function finishGapStats(
  matrix: { source_name: string; cells: EventRaceSummaryCell[] }[]
): { source_name: string; mean: number; std: number; n: number }[] {
  const points: { source_name: string; mean: number; std: number; n: number }[] = [];
  for (const row of matrix) {
    const gaps = (row.cells || [])
      .filter((cell) => cellFinished(cell) && cell.gap_total_m != null && Number.isFinite(cell.gap_total_m))
      .map((cell) => Number(cell.gap_total_m));
    const mean = meanOf(gaps);
    const std = stdOf(gaps);
    if (mean == null || std == null) continue;
    points.push({ source_name: row.source_name, mean, std, n: gaps.length });
  }
  return points;
}
